/**
 * Map staff sidebar / routes to subscription module `url` slugs
 * (see ModulesTableSeeder: loan_management, contribution, …).
 *
 * Parent modules gate top-level sidebar items; sub_module_urls gate sub-menus.
 * Dashboard is opt-in per role (can_view_dashboard); Super Admin always has it.
 * Super Admin uses the same module list as other roles (synced from Manager assignments).
 * Only role management remains Super Admin–only.
 */

import { sideBarData } from "./datautils";
import { filterStaffSidebarByCapabilities } from "./investorPortalRouteCapabilities";

export const ALWAYS_VISIBLE_STAFF_PATH_PREFIXES = [
  "/super_admin/notifications",
  "/super_admin/profile",
  "/super_admin/audit_log",
];

export const STAFF_DASHBOARD_PATH = "/super_admin/dashboard";

/** Prefer longer prefixes — order alone is not enough; getModuleUrlFromPath uses longest match. */
export const STAFF_PATH_PREFIX_TO_MODULE_URL = [
  ["/super_admin/settings", "configuration_and_settings"],
  ["/super_admin/member_management", "member_management"],
  ["/super_admin/staff_registration", "staff_management"],
  ["/super_admin/contribution", "contribution"],
  ["/super_admin/loan_management", "loan_management"],
  ["/super_admin/communication", "communication"],
  ["/super_admin/event_calendar", "event_calendar"],
  ["/super_admin/attendance", "attendance"],
  ["/super_admin/automated_birthday", "automated_birthday"],
  ["/super_admin/eminutes", "e-minutes"],
  ["/super_admin/e-minute", "e-minutes"],
  ["/super_admin/e_minute", "e-minutes"],
  ["/super_admin/modules/in-development/financial-accounting", "financial_accounting"],
  ["/super_admin/modules/in-development/dividend-management", "dividend_management"],
  ["/super_admin/modules/in-development/commodities-management", "commodities_management"],
  ["/super_admin/modules/in-development/society-election", "society_election"],
  ["/super_admin/modules/in-development/gallery-sharing", "gallery_sharing"],
  ["/super_admin/modules/in-development/socio-services", "socio_services"],
  ["/super_admin/modules/in-development/other-services", "other_services"],
  ["/super_admin/reports", "reports"],
  ["/super_admin/admin", "support"],
  ["/super_admin/birthday", "automated_birthday"],
  ["/super_admin/automated", "automated_birthday"],
  ["/super_admin/report", "reports"],
  // Investor hub — longest-prefix wins (marketplace must not resolve as investment).
  ["/super_admin/investor_portal/investments", "investment"],
  ["/super_admin/investor_portal/participants", "investment"],
  ["/super_admin/investor_portal/marketplace", "marketplace"],
  ["/super_admin/investor_portal/requests/real-estate", "real_estate"],
  ["/super_admin/investor_portal/requests", "real_estate"],
  ["/super_admin/investor_portal/investors", "investor_management"],
  ["/super_admin/investor_portal/website", "investor_management"],
  ["/super_admin/investor_portal/dashboard", "investor_management"],
  ["/super_admin/investor_portal", "investor_management"],
];

/** Parent module url → base path for sub-route extraction. */
export const PARENT_MODULE_PATH_PREFIX = {
  configuration_and_settings: "/super_admin/settings",
  loan_management: "/super_admin/loan_management",
  contribution: "/super_admin/contribution",
  member_management: "/super_admin/member_management",
  staff_management: "/super_admin/staff_registration",
  communication: "/super_admin/communication",
  attendance: "/super_admin/attendance",
  event_calendar: "/super_admin/event_calendar",
  automated_birthday: "/super_admin/automated_birthday",
  "e-minutes": "/super_admin/eminutes",
  reports: "/super_admin/reports",
  support: "/super_admin/admin",
  investment: "/super_admin/investor_portal/investments",
  marketplace: "/super_admin/investor_portal/marketplace",
  real_estate: "/super_admin/investor_portal/requests",
  investor_management: "/super_admin/investor_portal/investors",
  financial_accounting: "/super_admin/modules/in-development/financial-accounting",
  dividend_management: "/super_admin/modules/in-development/dividend-management",
  commodities_management: "/super_admin/modules/in-development/commodities-management",
  society_election: "/super_admin/modules/in-development/society-election",
  gallery_sharing: "/super_admin/modules/in-development/gallery-sharing",
  socio_services: "/super_admin/modules/in-development/socio-services",
  other_services: "/super_admin/modules/in-development/other-services",
};

/**
 * Map sidebar path segment (after parent prefix) → module sub_module `url` in DB.
 */
export const STAFF_SUB_MODULE_PATH_ALIASES = {
  portal_settings: "portal_setting",
  fine_settings: "fine_setting",
  loan_application_settings: "loan_application_setting",
  pending_member: "pending_members",
  members: "view_member",
  // Contribution path segments → catalog urls (after modules:repair-catalog).
  "contribution-category": "contribution_category",
  contribution_category: "contribution_category",
  contribution_history: "contribution_history",
  contribution_history_v2: "contribution_summary",
  record_contribution: "record_contribution",
  withdrawal_requests: "withdrawal_requests",
  branch_settlement_wallet: "branch_settlement_wallet",
  bank_transfer_confirmations: "bank_transfer_confirmations",
  online_payment_requery: "online_payment_requery",
  // Staff management
  staff_registration: "staff_registration",
  view_staff: "view_staff",
  batch_upload: "batch_upload",
  // E-minutes
  new: "create_message",
  view_minutes: "view_minutes",
  create_message: "create_message",
  group_management: "group_management",
  history: "attendance_history",
  sms_history: "sms_history",
  loan_type: "loan_type",
  record_existing_loan: "record_existing_loan",
  archived_members: "archived_members",
  exit_requests: "exit_requests",
  exited_members: "exited_members",
  member_slot_capacity: "member_slot_capacity",
  support_settings: "support_settings",
  opportunities: "opportunities",
  requests: "requests",
  participants: "participants",
  products: "products",
  orders: "orders",
  categories: "categories",
  investor_accounts: "investor_accounts",
  kyc_reviews: "kyc_reviews",
  website_testimonials: "website_testimonials",
  testimonials: "website_testimonials",
  kyc: "kyc_reviews",
  dashboard: "investor_accounts",
  "real-estate": "requests",
};

/**
 * Sidebar display name → module url (must match API module.url values).
 */
export const STAFF_SIDEBAR_MODULE_URLS = {
  "Configuration & Settings": "configuration_and_settings",
  "Member Management": "member_management",
  "Staff Management": "staff_management",
  "Contribution Management": "contribution",
  "Loan Management": "loan_management",
  "Communication Management": "communication",
  "Event Calendar": "event_calendar",
  Attendance: "attendance",
  "Automated Birthday": "automated_birthday",
  "E-Minutes": "e-minutes",
  "E-minutes": "e-minutes",
  "Financial Accounting": "financial_accounting",
  "Dividend Management": "dividend_management",
  "Commodities Management": "commodities_management",
  "Society Election": "society_election",
  "Gallery Sharing": "gallery_sharing",
  "Socio-Services": "socio_services",
  "Other Services": "other_services",
  "Reporting & Analytics": "reports",
  Support: "support",
  Communication: "communication",
  Contribution: "contribution",
  Reports: "reports",
  Investments: "investment",
  "Investor Management": "investor_management",
  Marketplace: "marketplace",
  "Real Estate": "real_estate",
};

export function isStaffSystemRole(role) {
  if (!role || typeof role !== "object") return false;
  if (role.is_system === true || role.is_system === 1 || role.is_system === "1") {
    return true;
  }
  if (String(role.alias || "").toLowerCase() === "super_admin") return true;
  return String(role.name || "").trim().toLowerCase() === "super admin";
}

/**
 * Super Admin or Role Management entitlement (configuration_and_settings → role_management).
 */
export function staffCanManageRoles(role) {
  if (!role) return false;
  if (isStaffSystemRole(role)) return true;

  const modules = Array.isArray(role.modules) ? role.modules : [];
  for (const mod of modules) {
    const parentUrl = String(mod?.url || "").toLowerCase();
    if (parentUrl !== "configuration_and_settings") continue;

    const subUrls = (mod.sub_module_urls || [])
      .map((u) => String(u).toLowerCase())
      .filter(Boolean);

    // Empty sub list = whole parent granted (includes Role Management).
    if (subUrls.length === 0) return true;
    if (subUrls.includes("role_management")) return true;
  }

  return false;
}

export function staffCanViewDashboard(role) {
  if (!role) return false;
  if (isStaffSystemRole(role)) return true;
  return Boolean(role.can_view_dashboard);
}

export function isStaffDashboardPath(path = "") {
  const normalized = String(path).replace(/\/$/, "");
  return (
    normalized === STAFF_DASHBOARD_PATH ||
    normalized.startsWith(`${STAFF_DASHBOARD_PATH}/`)
  );
}
export function getRoleModuleUrls(role) {
  if (!role) {
    return {
      isSystem: false,
      urls: new Set(),
      subsByParent: new Map(),
    };
  }

  // Match backend CompanyRole::isSystemRole() — do not rely on is_system alone
  // (prod DB may not have flagged Super Admin yet; login cache may omit the flag).
  const isSystem = isStaffSystemRole(role);
  const modules = Array.isArray(role.modules) ? role.modules : [];
  const urls = new Set(
    modules.map((m) => (m?.url || "").toLowerCase()).filter(Boolean)
  );
  const subsByParent = new Map();

  modules.forEach((mod) => {
    const parentUrl = (mod?.url || "").toLowerCase();
    if (!parentUrl) return;

    const subUrls = new Set(
      (mod.sub_module_urls || [])
        .map((url) => String(url).toLowerCase())
        .filter(Boolean)
    );
    subsByParent.set(parentUrl, subUrls);
  });

  return { isSystem, urls, subsByParent };
}

export const INVESTOR_HUB_MODULE_URLS = [
  "investment",
  "marketplace",
  "real_estate",
  "investor_management",
];

export function isInvestorHubPath(path = "") {
  const normalized = String(path).replace(/\/$/, "");
  return (
    normalized === "/super_admin/investor_portal" ||
    normalized.startsWith("/super_admin/investor_portal/")
  );
}

export function staffHasAnyInvestorHubModule(roleMeta) {
  return INVESTOR_HUB_MODULE_URLS.some((url) => staffHasModuleUrl(roleMeta, url));
}

export function staffHasModuleUrl(roleMeta, moduleUrl) {
  if (!moduleUrl) return false;
  return roleMeta.urls.has(String(moduleUrl).toLowerCase());
}

export function isAlwaysVisibleStaffPath(path = "") {
  const normalized = String(path).replace(/\/$/, "");
  return ALWAYS_VISIBLE_STAFF_PATH_PREFIXES.some(
    (p) => normalized === p || normalized.startsWith(`${p}/`)
  );
}

export function getModuleUrlFromPath(path = "") {
  const normalized = String(path).replace(/\/$/, "");
  if (!normalized) return null;

  let bestUrl = null;
  let bestLen = -1;
  for (const [prefix, moduleUrl] of STAFF_PATH_PREFIX_TO_MODULE_URL) {
    if (
      (normalized === prefix || normalized.startsWith(`${prefix}/`)) &&
      prefix.length > bestLen
    ) {
      bestUrl = moduleUrl;
      bestLen = prefix.length;
    }
  }

  return bestUrl;
}

export function normalizeSubModulePathSegment(segment = "") {
  const slug = String(segment).toLowerCase();
  return STAFF_SUB_MODULE_PATH_ALIASES[slug] || slug;
}

export function resolveSubModuleUrlFromPath(parentModuleUrl, path = "") {
  const parentUrl = String(parentModuleUrl || "").toLowerCase();
  const normalized = String(path).replace(/\/$/, "");
  const parentPrefix = PARENT_MODULE_PATH_PREFIX[parentUrl];
  if (!parentPrefix || !normalized.startsWith(parentPrefix)) {
    return null;
  }

  if (normalized === parentPrefix) {
    return null;
  }

  const remainder = normalized.slice(parentPrefix.length + 1);
  const segment = remainder.split("/")[0];
  if (!segment) return null;

  return normalizeSubModulePathSegment(segment);
}

/**
 * Whether staff may access a path under a parent module (respects sub_module_urls).
 * Empty / missing sub_module_urls = whole parent enabled.
 * Super Admin: parent module entitlement is enough for all children under that parent.
 */
export function staffCanAccessModulePath(roleMeta, path = "") {
  if (isAlwaysVisibleStaffPath(path)) return true;

  const parentUrl = getModuleUrlFromPath(path);
  if (!parentUrl) {
    // Unmapped routes (e.g. transaction history, in-dev stubs): Super Admin only.
    return Boolean(roleMeta.isSystem);
  }
  if (!staffHasModuleUrl(roleMeta, parentUrl)) return false;

  // Super Admin mirrors company modules at parent level — full subtree.
  if (roleMeta.isSystem) {
    return true;
  }

  const allowedSubs = roleMeta.subsByParent.get(parentUrl.toLowerCase());
  // Whole-module grant: no sub-module restrictions configured for this parent.
  if (!allowedSubs || allowedSubs.size === 0) {
    return true;
  }

  const subUrl = resolveSubModuleUrlFromPath(parentUrl, path);
  if (!subUrl) {
    return true;
  }

  return allowedSubs.has(subUrl.toLowerCase());
}

export function resolveSidebarItemModuleUrl(item) {
  if (!item || item.type === "section") return null;
  if (item.moduleUrl) return item.moduleUrl;
  if (Object.prototype.hasOwnProperty.call(STAFF_SIDEBAR_MODULE_URLS, item.name)) {
    return STAFF_SIDEBAR_MODULE_URLS[item.name];
  }
  return getModuleUrlFromPath(item.path);
}

function canShowSidebarSubItem(sub, parentModuleUrl, roleMeta) {
  if (sub.type === "section") return true;

  if (isAlwaysVisibleStaffPath(sub.path || "")) return true;

  if (!parentModuleUrl || !staffHasModuleUrl(roleMeta, parentModuleUrl)) {
    return false;
  }

  return staffCanAccessModulePath(roleMeta, sub.path || "");
}

function canShowSidebarParentItem(item, role, roleMeta) {
  if (item.type === "section") return true;

  const path = item.path || "";
  if (isStaffDashboardPath(path)) {
    return staffCanViewDashboard(role);
  }
  if (isAlwaysVisibleStaffPath(path)) return true;

  const parentModuleUrl = resolveSidebarItemModuleUrl(item);

  // Unmapped placeholder menus (Transaction History, in-development stubs):
  // Super Admin only — custom roles must have an explicit module mapping.
  if (!parentModuleUrl) {
    if (!roleMeta.isSystem) return false;
    if (item.subMenu?.length) {
      return item.subMenu.some((sub) => sub.type !== "section" && sub.path);
    }
    return Boolean(path);
  }

  if (!staffHasModuleUrl(roleMeta, parentModuleUrl)) {
    return false;
  }

  if (item.subMenu?.length) {
    return item.subMenu.some((sub) =>
      canShowSidebarSubItem(sub, parentModuleUrl, roleMeta)
    );
  }

  return staffCanAccessModulePath(roleMeta, path);
}

/**
 * Filter sidebar after capability filter.
 */
export function filterStaffSidebarByRoleModules(sideBarData, role) {
  const roleMeta = getRoleModuleUrls(role);

  if (!Array.isArray(role?.modules)) {
    return sideBarData.filter((item) => {
      if (item.type === "section") return false;
      return isAlwaysVisibleStaffPath(item.path || "");
    });
  }

  return sideBarData
    .map((item) => {
      if (item.type === "section") {
        return item;
      }

      if (!canShowSidebarParentItem(item, role, roleMeta)) {
        return null;
      }

      if (item.subMenu?.length) {
        const parentModuleUrl = resolveSidebarItemModuleUrl(item);
        const subMenu = item.subMenu.filter((sub) =>
          canShowSidebarSubItem(sub, parentModuleUrl, roleMeta)
        );

        const hasNavItem = subMenu.some((sub) => sub.type !== "section");
        if (!hasNavItem) return null;

        return { ...item, subMenu };
      }

      return item;
    })
    .filter(Boolean);
}

/**
 * Required module url for a staff path, or null if unrestricted.
 */
export function getRequiredModuleUrlForPath(pathname) {
  const path = (pathname || "").replace(/\/$/, "");
  if (!path.startsWith("/super_admin")) return null;

  if (isStaffDashboardPath(path)) {
    return "__dashboard__";
  }

  if (isAlwaysVisibleStaffPath(path)) {
    return null;
  }

  return getModuleUrlFromPath(path);
}

export function canAccessStaffPath(pathname, role) {
  const path = (pathname || "").replace(/\/$/, "");
  if (!path.startsWith("/super_admin")) return true;

  if (isStaffDashboardPath(path)) {
    return staffCanViewDashboard(role);
  }

  if (isAlwaysVisibleStaffPath(path)) return true;

  const roleMeta = getRoleModuleUrls(role);
  return staffCanAccessModulePath(roleMeta, path);
}

/**
 * First safe landing route for staff without dashboard access.
 */
export function resolveStaffHomePath(role, capabilities = null) {
  if (staffCanViewDashboard(role)) {
    return STAFF_DASHBOARD_PATH;
  }

  const filtered = filterStaffSidebarByRoleModules(
    filterStaffSidebarByCapabilities(sideBarData, capabilities),
    role
  );

  for (const item of filtered) {
    if (item.type === "section") continue;
    if (isStaffDashboardPath(item.path)) continue;

    if (item.subMenu?.length) {
      const firstSub = item.subMenu.find(
        (sub) => sub.type !== "section" && sub.path && !sub.path.includes("/role_management")
      );
      if (firstSub?.path) return firstSub.path;
    }

    if (item.path && !isStaffDashboardPath(item.path)) {
      return item.path;
    }
  }

  return "/super_admin/profile";
}

/**
 * Normalize staff profile payloads from login / profile API.
 */
export function normalizeStaffProfilePayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.user && (payload.user.staff_id || payload.user.role)) {
    return payload.user;
  }
  return payload;
}
